import Phaser from "phaser";
import { GameState } from "../state/GameState";
import { Palette } from "../ui/palette";
import { TILESET_KEY, Prop } from "../tiles";
import { buildMidFloorBodies, isMidFloorTile, midFloorSurfaceY } from "./../ui/midFloor";
import { renderTiledAuthoredContent, createTiledText } from "../ui/tiledRender";
import { RELAY, RELAY_ORDER, registerRelayAnims, type RelayKey } from "../ui/relay";
import { registerCharacterAnims, useCharacterSprite, updateCharacterAnim } from "../ui/characterAnim";
import { registerLucasAnims, useLucasSprite, updateLucasAnim, A as LucasAnim } from "../ui/lucasAnim";
import { registerAndrewAnims, useAndrewSprite, AA, andrewThrowBall, andrewAnimMs } from "../ui/andrewAnim";
import { Tex, collectibleTex, ensureSheepyAnim, SHEEPY_ANIM } from "../ui/textures";
import { baseValueForKind } from "../state/scoring";
import { registerPortraitAnims, type PortraitKey } from "../ui/portraits";
import { objClass } from "./BaseLevelScene";

/** Kirby's reactions used in the in-level video-call panel. He blinks on a regular
 *  cadence (like a real person), plays one bigger action now and then, and cheers
 *  right after each high-five. */
const KIRBY_ANIMS: PortraitKey[] = ["kirby-blink", "kirby-drink", "kirby-cheer", "kirby-clap"];
/** Bigger idle actions, played one at a time with long stillness between: sip water
 *  or a clap. (Blinking is handled separately, on its own faster cadence.) */
const KIRBY_IDLE_ACTIONS: PortraitKey[] = ["kirby-drink", "kirby-clap"];
/** How often Kirby blinks while at rest (ms, randomized). Kept short so the blink
 *  actually reads on screen instead of being a rare event. */
const KIRBY_BLINK_MS: [number, number] = [2200, 4200];
/** Encouraging reactions played right after a high-five. */
const KIRBY_CHEERS: PortraitKey[] = ["kirby-cheer", "kirby-clap"];
/** Kirby's on-call scale (bumped up so he fills the call; was 0.58, then 0.87). */
const KIRBY_SCALE = 1.05;
/** Pixels to nudge Kirby up within the call panel (his shoulders sat a touch low). */
const KIRBY_Y_NUDGE = 2;

/** Height of the shared HUD bar (matches BaseLevelScene's HUD_BAR_H). */
const HUD_BAR_H = 40;
/** Total relay countdown in seconds. */
const RELAY_TIME_S = 180;
/** Highlight yellow for the big HUD countdown. */
const HUD_YELLOW = "#ffd23f";
/** Fallback segment width, only used if relay.tmj has no authored handoff objects. */
const SEGMENT_W = 1600;
/** How far right of a handoff meeting point the incoming receiver waits. */
const RECEIVER_GAP = 70;
/** How far before a handoff meeting point the outgoing runner's auto-run to the
 *  high-five kicks in (the boundary trigger sits this far left of the meet point). */
const HANDOFF_TRIGGER_LEAD = 120;
/** Movement tuning, matching Level1DanScene so the feel is consistent. */
const RUN_SPEED = 290;
const JUMP_SPEED = 430;
/** Vertical speed while climbing a ladder tile (matches Level2LucasScene). */
const CLIMB_SPEED = 180;
/** Auto-run speed the outgoing runner uses to reach the meeting point. */
const HANDOFF_RUN = 240;
/** Andrew's steady auto-run speed in the relay. Unlike his own Level3AndrewScene,
 *  he does NOT ramp up to the finish here - he runs at a constant relay pace. */
const ANDREW_RELAY_SPEED = RUN_SPEED;
/** On-screen size (px) of Andrew's thrown Claude token, matching his level. */
const ANDREW_TOKEN_PX = 23;
/** How long the handoff camera pan takes, ms. Fits well inside the tag animations. */
const HANDOFF_PAN_MS = 650;
/** Fallback floor line (world y). The real value is read from the spawn object's y
 *  in relay.tmj (see parseLayout), so the Tiled file is WYSIWYG with the game. */
const FLOOR_TOP = 480;
/** Target on-screen height (px, head to feet) EVERY runner renders at during the
 *  relay - playable character and high-five sprite alike - so the two never look
 *  mismatched at a handoff. Each character keeps its native size in its own level;
 *  this normalization only applies here. */
const RELAY_PERSON_H = 150;
/** Measured head-to-feet px of each high-five sheet's standing frame (frame 0). Used
 *  to scale every tag sprite to RELAY_PERSON_H (the sheets are drawn at inconsistent
 *  sizes, e.g. Lucas is 255px tall tagging in but 241 tagging out). Re-measure if the
 *  art is re-packed: opaque bounding-box height of frame 0. */
const TAG_BODY_H: Record<RelayKey, number> = {
  "dan-tag-out": 235,
  "lukas-tag-in": 255,
  "lukas-tag-out": 241,
  "andrew-tag-in": 214,
};
/** Per-tag on-screen position nudge (px), for fine-aligning a high-five sprite.
 *  Lucas's high-five sits 2px right / 1px low, so shift it left + up. */
const TAG_NUDGE: Partial<Record<RelayKey, { dx: number; dy: number }>> = {
  "lukas-tag-in": { dx: -1, dy: -5 },
  "lukas-tag-out": { dx: -2, dy: -1 },
};
/** Relay-only playable scale nudges so each runner also lands on RELAY_PERSON_H. Dan
 *  (~147px) and Andrew (~149px) already sit there; Lucas runs ~10px taller, so trim
 *  him. Measured against each atlas's run-frame opaque height. */
const RELAY_PLAYABLE_SCALE: Record<string, number> = { dan: 1, lucas: 0.95, andrew: 1 };
/** Frame rate for the high-five tag anims. The shared anims register at 20fps
 *  (see relay.ts); we override per-play here so both the outgoing tag-out and the
 *  incoming tag-in read slower and more legible in the relay. Tune freely. */
const TAG_FPS = 12;
/** Andrew's standing art is drawn ~13% taller than his running art inside the
 *  uniform 192px atlas frame (measured: idle content 171px vs run ~151px), so at a
 *  fixed scale he reads bigger when idle. Shrink the idle pose toward the running
 *  height, pivoting on his feet so they stay planted. Target is run height + ~5px:
 *  matching run exactly read a touch too small, so idle sits slightly taller. */
const ANDREW_IDLE_MATCH = 156 / 171;

/** On-screen size (px) of the data-vault key pickup in part 2 (matches Lucas's keys). */
const VAULT_KEY_PX = 92;
/** The data-vault door interaction zone (px). Keep it tight so the player has to
 *  stand close to the door to trigger it. */
const VAULT_ZONE_W = 240;
const VAULT_ZONE_H = 200;
/** Tint that matches relay-bg-mid's authored `darken: 0.2`, so the open-vault overlay
 *  blends into the darkened background instead of reading brighter. */
const VAULT_OVERLAY_TINT = 0xcccccc;

type CharId = "dan" | "lucas" | "andrew";

/** How long (ms) BEFORE the giver's tag-out finishes the incoming runner starts
 *  their tag-in, so the two hands meet in the middle instead of the clips playing
 *  strictly back-to-back. Keyed by the incoming runner so each high-five can be
 *  tuned on its own (Dan starts the relay and never tags in). Larger = more overlap;
 *  the start is clamped so the tag-in never begins before the tag-out does. For
 *  reference, tag-out lengths at TAG_FPS 12 are: dan-tag-out ~1.58s (Lucas tags in),
 *  lukas-tag-out ~1.25s (Andrew tags in). Tweak these freely. */
const TAG_IN_LEAD_MS: Record<CharId, number> = {
  dan: 0, // never receives a tag-in
  lucas: 3000, // exceeds Dan's ~1.58s tag-out, so Lucas raises from the very start
  andrew: 1300, // exceeds Lucas's ~1.25s tag-out, so Andrew raises from the very start (his tag-in is the longer clip, so starting together lines the hands up)
};

/**
 * The Kirby relay level (T-208). ONE continuous world split into three visual
 * segments (Dan → Lucas → Andrew). Each runner traverses their segment, then hands
 * off to the next with a high-five (tag-out → tag-in) played in place, with the
 * camera held so there is no cut. Andrew's exit wins → the existing Finale.
 *
 * Everything lives in this single scene: there is NO scene.start between segments,
 * only the internal `phase` state advances, so the handoffs are seamless. Public
 * fields (player / currentChar / phase / worldWidth) are exposed for headless
 * verification.
 */
export class RelayScene extends Phaser.Scene {
  public player!: Phaser.Physics.Arcade.Sprite;
  public currentChar: CharId = "dan";
  /** Coarse progress marker for the verifier: "dan" | "handoff-0-out" |
   *  "handoff-0-in" | "lucas" | "handoff-1-out" | "handoff-1-in" | "andrew" | "won". */
  public phase = "dan";
  /** World bounds + segment layout, read from relay.tmj in create() (see parseLayout).
   *  Defaults mirror the old code constants so the scene still works without objects. */
  public worldWidth = 3 * SEGMENT_W;
  public segmentWidth = SEGMENT_W;
  /** Authored layout, parsed from the "objects" layer of relay.tmj. */
  private spawnX = 120;
  private meets: number[] = [SEGMENT_W - 40, 2 * SEGMENT_W - 40]; // handoff meeting points
  private exitX = 3 * SEGMENT_W - 120;
  private floorTop = FLOOR_TOP; // floor surface y, taken from the spawn object's y

  private world?: Phaser.Tilemaps.TilemapLayer; // tile-driven floor collision (invisible)
  private midFloor?: Phaser.Physics.Arcade.StaticGroup; // mid-anchor tile bodies (feet on tile middle)
  private worldFirstgid = 1; // office tileset firstgid, for local-id tile checks (mid-floor)
  // Tag sprites live for the whole level so every runner is always on screen. Each
  // receiver stands at its handoff point on frame 0 of its tag-in from the start
  // (just waiting); each giver takes the outgoing player's place and is left standing
  // afterwards. Indexed by handoff: [0] = dan→lucas, [1] = lucas→andrew.
  private givers: Phaser.GameObjects.Sprite[] = [];
  private receivers: Phaser.GameObjects.Sprite[] = [];
  private kirby?: Phaser.GameObjects.Sprite; // in-level Kirby video call (whole level)
  private kirbyTimer?: Phaser.Time.TimerEvent; // ambient idle/drink cadence
  private kirbyBlinkTimer?: Phaser.Time.TimerEvent; // regular blink cadence
  private kirbyCelebrating = false; // true while playing a post-high-five cheer
  private kirbyActing = false; // true while a bigger idle action (drink/clap) plays
  private locked = false; // true during a handoff: ignore input
  private climbing = false; // true while the playable runner is on a ladder tile
  private runningOut = false; // outgoing runner auto-running to the meeting point
  private meetX = 0; // x the outgoing runner stops at to tag out
  private firedHandoff = [false, false];
  private won = false;

  // Part 2 (Lucas segment) data-vault door: collect the key, touch the vault to open
  // it (reveals the interior). Mirrors Level2LucasScene's garage-door key gating.
  private hasVaultKey = false;
  private vaultOpen = false;
  private vaultLockedMsgAt = 0; // throttles the "locked" hint while overlapping the vault
  private notice?: Phaser.GameObjects.Text; // HUD-bar flash text (marquees when too wide)
  private noticeTween?: Phaser.Tweens.Tween;
  private noticeFade?: Phaser.Time.TimerEvent;
  private hudScore?: Phaser.GameObjects.Text;
  private sheepyCount = 0;
  private relayScore = 0;
  private relayCollected = 0;
  private claudeMegaPos?: { x: number; y: number };

  // 3-minute countdown timer
  private timerStart = 0; // time.now when the countdown began
  private hudTimer?: Phaser.GameObjects.Text;

  // Andrew's Claude-token throw (pre-equipped in the relay). He owns jump + throw
  // like in his own level; the token is a spinning Claude sprite that flies forward.
  private andrewThrowUntil = 0; // time.now the throw pose holds until
  private andrewThrownFor = -1; // throwUntil value already launched, so each throw fires once
  private andrewHeldBall?: Phaser.GameObjects.Image; // the token in Andrew's hand mid-throw

  // Andrew's base render transform (captured after useAndrewSprite), so the idle
  // size-match override can be applied and cleanly restored when he moves.
  private andrewBaseScale = 1;
  private andrewBaseOriginX = 0.5;
  private andrewBaseOriginY = 0.5;

  private keys!: {
    cursors: Phaser.Types.Input.Keyboard.CursorKeys;
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
    jump: Phaser.Input.Keyboard.Key;
    throw: Phaser.Input.Keyboard.Key;
  };

  constructor() {
    super("Relay");
  }

  create() {
    this.locked = false;
    this.climbing = false;
    this.runningOut = false;
    this.won = false;
    this.firedHandoff = [false, false];
    this.currentChar = "dan";
    this.phase = "dan";
    this.andrewThrowUntil = 0;
    this.andrewThrownFor = -1;
    this.andrewHeldBall = undefined;
    this.hasVaultKey = false;
    this.vaultOpen = false;
    this.vaultLockedMsgAt = 0;
    this.sheepyCount = 0;
    this.relayScore = 0;
    this.relayCollected = 0;
    this.claudeMegaPos = undefined;
    // Reset the Kirby-call handles (the scene instance is reused across restarts).
    this.kirby = undefined;
    this.kirbyTimer?.remove();
    this.kirbyTimer = undefined;
    this.kirbyBlinkTimer?.remove();
    this.kirbyBlinkTimer = undefined;
    this.kirbyCelebrating = false;
    this.kirbyActing = false;

    // Tiled scenery (public/assets/maps/relay.tmj): parallax backgrounds, the tiled
    // floor, AND the authored layout objects (spawn / handoff points / exit). One map
    // instance is parsed for layout and reused for the scenery so both stay in sync.
    const map = this.make.tilemap({ key: "relay" });
    this.parseLayout(map);
    this.buildScenery(map);

    const W = this.worldWidth;
    const H = map.heightInPixels;
    this.physics.world.setBounds(0, 0, W, H);
    // Camera follows the player across the full map; on the flat floor it settles at
    // the bottom (floor low, skyline above), and reveals more sky if a runner jumps.
    this.cameras.main.setBounds(0, 0, W, H);
    this.cameras.main.setBackgroundColor(Palette.bg);

    // Relay handoff (tag) animations, plus every runner's tag sprite placed up front
    // so nobody pops in or out during a high-five. Receivers stand at their meeting
    // point on frame 0 of their tag-in; givers start hidden and appear in place of the
    // outgoing player when it is their turn to tag out.
    registerRelayAnims(this);
    this.givers = [
      this.makeTag(RELAY_ORDER[0].tagOut!, this.meetXFor(0)).setVisible(false),
      this.makeTag(RELAY_ORDER[1].tagOut!, this.meetXFor(1)).setVisible(false),
    ];
    this.receivers = [
      this.makeTag(RELAY_ORDER[1].tagIn!, this.receiverXFor(0)).setDepth(9),
      this.makeTag(RELAY_ORDER[2].tagIn!, this.receiverXFor(1)),
    ];

    // The player. Created as Dan; setCharacter swaps atlas/body/scale per runner.
    this.player = this.physics.add.sprite(this.spawnX, this.floorTop - 80, "char-dan", "dan-idle-0");
    this.player.setCollideWorldBounds(true);
    // Player vs the tile floor. Solid `collides` tiles always stop the player; `oneway`
    // tiles only stop a downward landing (hold down to drop through), same as the levels.
    if (this.world) {
      this.physics.add.collider(this.player, this.world, undefined, (_p, tileObj) => {
        const tile = tileObj as Phaser.Tilemaps.Tile;
        if (!tile.properties || !(tile.properties as Record<string, unknown>)[Prop.ONEWAY]) return true;
        if (this.keys.down.isDown || this.keys.cursors.down?.isDown) return false;
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        return body.velocity.y >= 0 && body.prev.y + body.height <= tile.pixelY + 4;
      });
    }
    // Player vs the mid-floor bodies: their top is the tile's middle (so the player rests
    // half a tile lower), and they are ONE-WAY like the jump-through platform: you land on
    // them from above, jump up through them, hold down to drop through, AND climb straight
    // through them on a ladder (so a mid-floor can cross a ladder run).
    if (this.midFloor) {
      this.physics.add.collider(this.player, this.midFloor, undefined, (_p, zoneObj) => {
        if (this.climbing) return false; // climbing a ladder passes straight through
        if (this.keys.down.isDown || this.keys.cursors.down?.isDown) return false; // drop through
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        const zbody = (zoneObj as Phaser.GameObjects.Zone).body as Phaser.Physics.Arcade.StaticBody;
        return body.velocity.y >= 0 && body.prev.y + body.height <= zbody.top + 4;
      });
    }
    this.setCharacter("dan");
    this.placeOnFloor(this.spawnX); // feet on the tile floor, clear of the collision tiles

    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

    // Part 2 collectible key + the data-vault door it unlocks (needs the player + physics).
    this.buildVaultProps(map);

    // The shared HUD (same dark bar + Tiled-authored readouts as the other levels).
    this.buildRelayHud();

    // The countdown starts on the player's first input, not at scene create,
    // so Kirby's idle call doesn't eat into the timer.
    this.timerStart = 0;

    // Kirby watches the whole relay over a video call, top-centre, from the start.
    this.showKirbyCall();

    this.buildInput();

    // Position Andrew's held Claude token after physics moves him each frame (so it
    // sits in his hand, not a frame behind), and launch it at the throw's release
    // frame. Only active during his segment; cleaned up on scene shutdown.
    this.events.on(Phaser.Scenes.Events.POST_UPDATE, this.updateAndrewThrowBall, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.events.off(Phaser.Scenes.Events.POST_UPDATE, this.updateAndrewThrowBall, this),
    );
  }

  /** Build the same HUD the game levels use: a dark top bar plus the Tiled-authored
   *  readouts from hud.tmj. The relay has no per-run scoring of its own, so the score
   *  cluster shows the player's accumulated run totals (points + stars). */
  private buildRelayHud() {
    const W = this.scale.width;
    this.add.rectangle(0, 0, W, HUD_BAR_H, 0x000000, 0.8).setOrigin(0, 0).setScrollFactor(0).setDepth(999);

    // Reuse the shared HUD map's authored text objects (position/font/colour from Tiled).
    const raw = this.cache.tilemap.get("hud")?.data as { layers?: Array<Record<string, unknown>> } | undefined;
    this.hudScore = undefined;
    for (const layer of raw?.layers ?? []) {
      if (layer.type !== "objectgroup") continue;
      for (const o of (layer.objects as Array<Record<string, unknown>>) ?? []) {
        if (!o.text) continue;
        const name = String((o.name as string) ?? "").toLowerCase();
        if (name === "score") this.hudScore = createTiledText(this, o).setScrollFactor(0).setDepth(1000);
        else if (name === "timer") this.hudTimer = createTiledText(this, o).setScrollFactor(0).setDepth(1000);
      }
    }

    if (!this.hudTimer) {
      this.hudTimer = this.add
        .text(W - 16, HUD_BAR_H / 2, "", {
          fontFamily: "monospace",
          fontSize: "26px",
          fontStyle: "bold",
          color: HUD_YELLOW,
        })
        .setOrigin(1, 0.5)
        .setScrollFactor(0)
        .setDepth(1000);
    }

    this.updateRelayHud();
  }

  private updateRelayHud() {
    const rows: Array<[string, string]> = [
      ["Score", String(this.relayScore)],
      ["Items", String(this.relayCollected)],
    ];
    const w = Math.max(...rows.map(([l, v]) => l.length + v.length)) + 2;
    const table = rows.map(([l, v]) => l + v.padStart(w - l.length)).join("\n");
    this.hudScore?.setText(table);
  }

  private addRelayPoints(pts: number) {
    this.relayScore += pts;
    this.relayCollected++;
    this.updateRelayHud();
  }

  private buildInput() {
    const kb = this.input.keyboard!;
    this.keys = {
      cursors: kb.createCursorKeys(),
      left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      jump: kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
      throw: kb.addKey(Phaser.Input.Keyboard.KeyCodes.F),
    };
    kb.on("keydown-ESC", () => this.scene.start("MarketSelect"));
    kb.on("keydown-N", () => this.winRelay()); // (dev) force-win, like the levels' N shortcut
  }

  /** Part 2 (Lucas segment): build the collectible key and the data-vault door from the
   *  relay.tmj "objects" layer (classes "key" and "data_vault_door"). Mirrors
   *  Level2LucasScene's garage door: the base art shows the vault CLOSED; grab the key,
   *  touch the vault, and the open interior is overlaid in place. */
  private buildVaultProps(map: Phaser.Tilemaps.Tilemap) {
    const objs = map.getObjectLayer("objects")?.objects ?? [];
    for (const obj of objs) {
      const cls = objClass(obj);
      if (cls === "key") this.makeVaultKey(obj);
      else if (cls === "data_vault_door") this.makeVaultDoor(obj);
      else if (cls === "sheepy") this.makeSheepy(obj);
      else if (cls === "claude_mega") this.makeClaudeMega(obj);
      else if (["laptop", "document", "feature", "money", "vw", "idea", "bionic"].includes(cls)) this.makeCollectible(obj, cls);
    }
  }

  /** The data-vault key: a bobbing pickup on the part-2 walkway. Walking over it arms
   *  the vault (like grabbing Lucas's garage key). */
  private makeVaultKey(obj: Phaser.Types.Tilemaps.TiledObject) {
    const key = this.physics.add.staticImage(obj.x!, obj.y!, Tex.key).setDepth(1);
    key.setScale(VAULT_KEY_PX / Math.max(key.width, key.height)).refreshBody();
    this.bobSprite(key);
    this.physics.add.overlap(this.player, key, () => {
      if (!key.active) return;
      key.destroy();
      this.hasVaultKey = true;
      this.addRelayPoints(25);
      this.toast("Grabbed the Bionic key, hand it to Lukas!", Palette.lucas);
    });
  }

  /** The data-vault door. The closed vault is painted into relay-bg-mid; touching this
   *  (invisible, non-blocking) zone with the key overlays the open interior. Without the
   *  key it just shows a throttled locked hint. Non-blocking so the relay never stalls. */
  private makeVaultDoor(obj: Phaser.Types.Tilemaps.TiledObject) {
    const cx = obj.x!;
    const cy = obj.y!;
    const zone = this.add.zone(cx, cy, VAULT_ZONE_W, VAULT_ZONE_H);
    this.physics.add.existing(zone, true);
    const overlap = this.physics.add.overlap(this.player, zone, () => {
      if (this.vaultOpen) return;
      if (!this.hasVaultKey) {
        if (this.time.now - this.vaultLockedMsgAt > 2000) {
          this.vaultLockedMsgAt = this.time.now;
          this.toast("The data vault is locked - find the key", Palette.textDim);
        }
        return;
      }
      this.vaultOpen = true;
      // Reveal the open interior. Depth just above the backdrop (-10) so it reads as part
      // of the wall behind the runner; tinted to match the backdrop's authored darken.
      this.add.image(cx, cy, Tex.dataVaultDoor).setDepth(-9).setTint(VAULT_OVERLAY_TINT);
      overlap.destroy();
      zone.destroy();
      this.toast("Data vault open! Grab the Claude token!", Palette.lucas);
      if (this.claudeMegaPos) this.spawnClaudeMega(this.claudeMegaPos.x, this.claudeMegaPos.y);
    });
  }

  /** A collectible Sheepy: animated sprite that bobs and is collected on overlap. */
  private makeSheepy(obj: Phaser.Types.Tilemaps.TiledObject) {
    ensureSheepyAnim(this);
    const s = this.physics.add.sprite(obj.x!, obj.y!, Tex.sheepy).setScale(0.56).setDepth(1);
    s.play(SHEEPY_ANIM);
    (s.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.bobSprite(s);
    this.physics.add.overlap(this.player, s, () => {
      if (!s.active) return;
      s.destroy();
      this.sheepyCount++;
      this.addRelayPoints(100);
      this.toast(`★ Sheepy! (${this.sheepyCount}/3) +100 ★`, Palette.text);
    });
  }

  private makeCollectible(obj: Phaser.Types.Tilemaps.TiledObject, kind: string) {
    const tex = collectibleTex(kind);
    const s = this.physics.add.sprite(obj.x!, obj.y!, tex).setDepth(1);
    s.setScale(28 / Math.max(s.width, s.height));
    (s.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.bobSprite(s);
    const pts = baseValueForKind(kind);
    this.physics.add.overlap(this.player, s, () => {
      if (!s.active) return;
      s.destroy();
      this.addRelayPoints(pts);
    });
  }

  /** Store the mega Claude token position from the map; the token itself is
   *  spawned when the vault opens (see makeVaultDoor). */
  private makeClaudeMega(obj: Phaser.Types.Tilemaps.TiledObject) {
    this.claudeMegaPos = { x: obj.x!, y: obj.y! };
  }

  /** Spawn the mega Claude coin inside the open vault (animated spinning coin). */
  private spawnClaudeMega(x: number, y: number) {
    const key = "claude-mega-spin";
    if (!this.anims.exists(key)) {
      this.anims.create({ key, frames: this.anims.generateFrameNumbers(Tex.claudeMega, { start: 0, end: 12 }).reverse(), frameRate: 12, repeat: -1 });
    }
    const s = this.physics.add.sprite(x, y, Tex.claudeMega).setDepth(5);
    const targetPx = 90;
    s.setScale(targetPx / 512);
    (s.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    s.play(key);
    this.bobSprite(s);
    this.physics.add.overlap(this.player, s, () => {
      if (!s.active) return;
      s.destroy();
      this.addRelayPoints(200);
      this.toast("Give this Claude coin to Andrew for unlimited AI tokens!", "#c896ff");
    });
  }

  private fadeToGameOver() {
    if (this.won) return;
    this.won = true;
    this.physics.pause();
    this.cameras.main.fade(600, 0, 0, 0, false, (_cam: unknown, progress: number) => {
      if (progress >= 1) this.scene.start("GameOver", { returnScene: "Relay" });
    });
  }

  /** A gentle bob for a pickup, matching Level2LucasScene's key/battery bob. */
  private bobSprite(target: Phaser.GameObjects.Components.Transform & { y: number }) {
    const amp = Phaser.Math.Between(7, 12);
    this.tweens.add({
      targets: target,
      y: target.y - amp,
      duration: Phaser.Math.Between(680, 1000),
      delay: Phaser.Math.Between(0, 500),
      yoyo: true,
      repeat: -1,
      ease: "Sine.inOut",
    });
  }

  /** HUD-bar flash text in the centre lane (between score and timer). Marquees
   *  automatically when the message is too wide for the lane. Matches BaseLevelScene's
   *  flashText behaviour. */
  private toast(message: string, color: number | string = Palette.text) {
    const css = typeof color === "number" ? Phaser.Display.Color.IntegerToColor(color).rgba : color;

    this.noticeTween?.remove();
    this.noticeFade?.remove();
    this.notice?.destroy();

    const scoreRight = this.hudScore ? this.hudScore.x + this.hudScore.width : 16;
    let clusterLeft = this.scale.width - 16;
    if (this.hudTimer) clusterLeft = Math.min(clusterLeft, this.hudTimer.x - this.hudTimer.width);
    const laneL = scoreRight + 16;
    const laneW = Math.max(160, clusterLeft - 16 - laneL);

    const t = this.add
      .text(laneL, HUD_BAR_H / 2, message, {
        fontFamily: "monospace",
        fontSize: "18px",
        color: css,
      })
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(1002)
      .setShadow(0, 1, "#000000", 3);
    this.notice = t;

    let hold = 3600;
    if (t.width <= laneW) {
      t.setX(laneL + (laneW - t.width) / 2);
    } else {
      const maxOffset = t.width - laneW;
      const pan = { o: 0 };
      t.setCrop(0, 0, laneW, t.height);
      this.noticeTween = this.tweens.add({
        targets: pan,
        o: maxOffset,
        duration: Math.max(1500, maxOffset * 14),
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
        onUpdate: () => {
          t.setX(laneL - pan.o);
          t.setCrop(pan.o, 0, laneW, t.height);
        },
      });
      hold = 7000;
    }

    this.noticeFade = this.time.delayedCall(hold, () => {
      this.noticeTween?.remove();
      this.tweens.add({
        targets: t,
        alpha: 0,
        duration: 420,
        onComplete: () => {
          t.destroy();
          if (this.notice === t) this.notice = undefined;
        },
      });
    });
  }

  /** Read the level layout authored in relay.tmj's "objects" layer: the spawn point,
   *  the handoff meeting points (one per high-five, ordered left→right), and the exit.
   *  Everything is WYSIWYG with Tiled - move a marker there and the game follows. Falls
   *  back to the previous hard-coded layout if the objects layer is missing. */
  private parseLayout(map: Phaser.Tilemaps.Tilemap) {
    this.worldWidth = map.widthInPixels || this.worldWidth;
    const objs = map.getObjectLayer("objects")?.objects ?? [];
    const byClass = (c: string) => objs.filter((o) => objClass(o) === c);
    const spawn = byClass("spawn")[0];
    const handoffs = byClass("handoff").sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
    const exit = byClass("exit")[0];
    if (spawn?.x != null) this.spawnX = spawn.x;
    if (spawn?.y != null) this.floorTop = spawn.y;
    if (handoffs.length >= 2) this.meets = handoffs.map((h) => h.x ?? 0);
    if (exit?.x != null) this.exitX = exit.x;
    this.segmentWidth = this.worldWidth / 3;
  }

  /** Render the Tiled scenery (parallax backdrops) and wire up the tile-driven floor.
   *  The "world" tile layer is the collision geometry, authored in Tiled exactly like
   *  the office/roof levels: any tile with the tileset `collides` property is solid,
   *  `oneway` tiles are jump-through. Tiles are visible (matching the other levels).
   *  Add or move tiles in relay.tmj's "world" layer to shape the level. */
  private buildScenery(map: Phaser.Tilemaps.Tilemap) {
    const tileset = map.addTilesetImage("office", TILESET_KEY);
    const world = tileset ? map.createLayer("world", tileset, 0, 0) : null;
    if (world) {
      world.setCollisionByProperty({ [Prop.COLLIDES]: true });
      world.setCollisionByProperty({ [Prop.ONEWAY]: true });
      world.setVisible(false);
      this.world = world;
      this.worldFirstgid = tileset ? tileset.firstgid : 1;
      // Mid-floor tiles (the last office tile) get a solid body over their bottom half,
      // so the player is anchored on the tile's middle. Collider added in create().
      this.midFloor = buildMidFloorBodies(this, world, this.worldFirstgid);
    }
    // Visual-only platforms layer (decorative, no collision).
    const platformsTs = map.addTilesetImage("platforms", "platforms-tiles");
    const tilesets = [tileset, platformsTs].filter(Boolean) as Phaser.Tilemaps.Tileset[];
    map.createLayer("platforms", tilesets, 0, 0)?.setDepth(1);

    // Image layers (the backdrops) render behind everything at depth -10.
    renderTiledAuthoredContent(this, map, "relay");
  }

  /** World y of the tile floor surface at column `x`: the top edge of the topmost
   *  `collides` tile in that column, i.e. exactly where a character's feet come to
   *  rest. Mid-floor tiles (the last office tile) anchor on their vertical MIDDLE, so
   *  their surface is half a tile lower. This is what seats the tag animations on the
   *  SAME floor the player lands on, so authoring the floor in Tiled moves the runners
   *  AND the handoff sprites together. Falls back to the authored floor line if empty. */
  private floorYAt(x: number): number {
    if (!this.world) return this.floorTop;
    const tileX = this.world.worldToTileX(x);
    for (let ty = 0; ty < this.world.layer.height; ty++) {
      const tile = this.world.getTileAt(tileX, ty);
      if (!tile) continue;
      if (isMidFloorTile(tile, this.worldFirstgid)) return midFloorSurfaceY(tile);
      if (tile.collides) return tile.pixelY;
    }
    return this.floorTop;
  }

  /** Snap the (already character-swapped) player so its feet rest exactly on the tile
   *  floor at world x, velocity zeroed. Used at spawn and each handoff so the incoming
   *  runner never starts overlapping the collision tiles (which would let it sink). The
   *  offset from sprite to feet differs per character, so it is measured, not assumed. */
  private placeOnFloor(x: number) {
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const floor = this.floorYAt(x);
    // Seat the feet flush on the tile floor with no gap, so the incoming runner appears
    // exactly where the receiver stood and never visibly drops in. (An earlier version
    // dropped the runner in from a gap to dodge a sink-through bug; the tile-driven floor
    // lands flush cleanly now, verified no sink.) We sync the physics body to the sprite's
    // CURRENT transform first (updateFromGameObject), then correct by the true body-bottom,
    // so the placement is exact regardless of each runner's per-character scale (Lucas is
    // scaled 0.95, which threw off a static offset formula).
    this.player.setPosition(x, floor);
    body.updateFromGameObject();
    this.player.y += floor - (body.y + body.height);
    body.updateFromGameObject();
    this.player.setVelocity(0, 0);
  }

  /** Idle atlas frame for each runner (their plain standing pose). */
  private static readonly IDLE_FRAME: Record<CharId, [tex: string, frame: string]> = {
    dan: ["char-dan", "dan-idle-0"],
    lucas: ["char-lucas", "lucas-idle-0"],
    andrew: ["char-andrew", "andrew-idle-0"],
  };

  /** Leave a tagged-out giver standing in its character's idle pose (feet on the floor)
   *  at the scale that character rendered at while playable, so the size matches. The
   *  tag-out sheets are run cycles with no standing frame, so we swap to the atlas idle. */
  private standGiverIdle(giver: Phaser.GameObjects.Sprite, id: CharId, scaleX: number, scaleY: number, feetY: number) {
    const [tex, frame] = RelayScene.IDLE_FRAME[id];
    giver.setTexture(tex, frame).setOrigin(0.5, 1).setScale(scaleX, scaleY).setFlipX(false);
    giver.y = feetY; // feet where the outgoing runner actually stood (robust to overhang columns)
  }

  /** Point the player sprite at the given runner's atlas/body/scale + animations. */
  private setCharacter(which: CharId) {
    this.currentChar = which;
    if (which === "dan") {
      registerCharacterAnims(this);
      useCharacterSprite(this.player);
      this.player.setScale(1); // DAN_SCALE/DAN_ART == 1 (art packed 3x)
    } else if (which === "lucas") {
      registerLucasAnims(this);
      useLucasSprite(this.player); // sets its own scale
    } else {
      registerAndrewAnims(this);
      useAndrewSprite(this.player); // sets its own scale + facing
    }
    // Relay-only size normalization so the playable runner matches the high-five
    // sprites (all land on RELAY_PERSON_H). Applied on top of each character's base
    // scale, before Andrew's base transform is captured for the idle-match override.
    this.player.setScale(this.player.scaleX * RELAY_PLAYABLE_SCALE[which]);
    if (which === "andrew") {
      this.andrewBaseScale = this.player.scaleX;
      this.andrewBaseOriginX = this.player.originX;
      this.andrewBaseOriginY = this.player.originY;
    }
    this.player.setDepth(10);
  }

  private leftDown() {
    return this.keys.cursors.left?.isDown || this.keys.left.isDown;
  }
  private rightDown() {
    return this.keys.cursors.right?.isDown || this.keys.right.isDown;
  }
  private jumpJustDown() {
    return (
      Phaser.Input.Keyboard.JustDown(this.keys.jump) ||
      Phaser.Input.Keyboard.JustDown(this.keys.up) ||
      Phaser.Input.Keyboard.JustDown(this.keys.cursors.up!)
    );
  }
  private upDown() {
    return this.keys.cursors.up?.isDown || this.keys.up.isDown;
  }
  private downDown() {
    return this.keys.cursors.down?.isDown || this.keys.down.isDown;
  }
  private throwJustDown() {
    return Phaser.Input.Keyboard.JustDown(this.keys.throw);
  }

  /** True when a `ladder` tile overlaps the player's body. Runners are several tiles
   *  tall, so a single centre sample misses; walk points down the body. Ladder tiles
   *  (office id 5) are non-solid, painted in the world layer over the backdrop's ladder. */
  private onLadder(): boolean {
    if (!this.world) return false;
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const x = body.center.x;
    const step = this.world.tilemap.tileHeight * 0.5;
    for (let y = body.top + 4; y <= body.bottom - 2; y += step) {
      const t = this.world.getTileAtWorldXY(x, y);
      if (t && t.properties?.[Prop.LADDER]) return true;
    }
    return false;
  }

  /** True when a ladder tile sits just below the feet, so holding down steps the runner
   *  off the ledge above onto the ladder. */
  private ladderBelow(): boolean {
    if (!this.world) return false;
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const t = this.world.getTileAtWorldXY(body.center.x, body.bottom + this.world.tilemap.tileHeight * 0.5);
    return !!t && !!t.properties?.[Prop.LADDER];
  }

  /** Animate the current runner while on a ladder. Lucas has a dedicated climb clip
   *  (a back view, frozen when not moving); Dan/Andrew have none, so they hold their
   *  idle pose upright (defensive: the authored ladder is in Lucas's segment). */
  private driveClimbAnim(moving: boolean) {
    if (this.currentChar === "lucas") {
      this.player.setFlipX(false);
      if (this.player.anims.currentAnim?.key !== LucasAnim.climb) this.player.play(LucasAnim.climb, true);
      if (moving) this.player.anims.resume();
      else this.player.anims.pause();
      return;
    }
    const [, frame] = RelayScene.IDLE_FRAME[this.currentChar];
    this.player.anims.stop();
    this.player.setFrame(frame);
    this.player.setFlipX(false);
  }

  override update() {
    // Start the timer on first player input (so Kirby's idle call doesn't eat time).
    if (this.timerStart === 0) {
      const k = this.keys;
      const any = k.left.isDown || k.right.isDown || k.up.isDown || k.down.isDown || k.jump.isDown || k.throw.isDown || k.cursors.left.isDown || k.cursors.right.isDown || k.cursors.up.isDown || k.cursors.down.isDown;
      if (any) this.timerStart = this.time.now;
    }

    // Countdown timer (shows full time until the countdown has started).
    const elapsed = this.timerStart ? (this.time.now - this.timerStart) / 1000 : 0;
    const remaining = Math.max(0, RELAY_TIME_S - elapsed);
    const mins = Math.floor(remaining / 60);
    const secs = Math.floor(remaining % 60);
    const warn = remaining <= 30;
    this.hudTimer?.setText(`${mins}:${secs.toString().padStart(2, "0")}`).setColor(warn ? "#ff5a5a" : HUD_YELLOW);
    if (remaining <= 0 && !this.won) {
      this.fadeToGameOver();
      return;
    }

    const body = this.player.body as Phaser.Physics.Arcade.Body;

    if (this.locked) {
      // During a handoff: no player input. The outgoing runner auto-runs right to
      // the meeting point, then stops for the tag-out.
      if (this.runningOut) {
        this.player.setVelocityX(HANDOFF_RUN);
        if (this.player.x >= this.meetX) {
          this.player.setVelocityX(0);
          this.runningOut = false;
          this.beginTagOut();
        }
      } else {
        this.player.setVelocityX(0);
      }
      this.driveAnim();
      return;
    }

    if (this.currentChar === "andrew") {
      // Andrew auto-runs right at a steady relay pace (no speed ramp - that only
      // happens in his own Level3AndrewScene). The player owns jump + throw, and he is
      // already equipped with Claude tokens, so a throw launches a Claude token.
      if (!body.allowGravity) body.setAllowGravity(true);
      this.player.setVelocityX(ANDREW_RELAY_SPEED);
      if (this.jumpJustDown() && body.blocked.down) this.player.setVelocityY(-JUMP_SPEED);
      if (this.throwJustDown()) {
        this.andrewThrowUntil = this.time.now + andrewAnimMs(this, AA.throw);
        this.player.play(AA.throw);
      }
      this.driveAndrewRunAnim(body);
    } else {
      // Ladder climbing. The relay's "world" layer carries the same tile behaviours as the
      // office/roof levels: `collides` (walls/floor), `oneway` (jump-through platforms) and
      // `ladder` (climbable, non-solid). Enter a climb when up/down is pressed while a ladder
      // overlaps the body (or pressing down with a ladder just below the feet, to step off a
      // ledge onto it); gravity is off while climbing, and a jump or clearing the ladder drops
      // out. Mirrors Level2LucasScene. Only Lucas has a climb clip; the authored ladder sits
      // in his segment, so others just hold their idle pose on the (defensive) other paths.
      const upHeld = this.upDown();
      const downHeld = this.downDown();
      const onLadder = this.onLadder();
      const stepDown = downHeld && this.ladderBelow();
      if (!this.climbing && ((upHeld && onLadder) || stepDown)) this.climbing = true;
      // Exit on a SPACE jump (not the up key, which climbs) or once off the ladder.
      if (this.climbing && (Phaser.Input.Keyboard.JustDown(this.keys.jump) || (!onLadder && !stepDown))) {
        this.climbing = false;
      }

      if (this.climbing) {
        body.setAllowGravity(false);
        this.player.setVelocityX(this.leftDown() ? -RUN_SPEED : this.rightDown() ? RUN_SPEED : 0);
        this.player.setVelocityY(upHeld ? -CLIMB_SPEED : downHeld ? CLIMB_SPEED : 0);
        this.driveClimbAnim(upHeld || downHeld);
      } else {
        if (!body.allowGravity) body.setAllowGravity(true);
        // Normal movement.
        if (this.leftDown()) this.player.setVelocityX(-RUN_SPEED);
        else if (this.rightDown()) this.player.setVelocityX(RUN_SPEED);
        else this.player.setVelocityX(0);

        if (this.jumpJustDown() && body.blocked.down) this.player.setVelocityY(-JUMP_SPEED);

        this.driveAnim();
      }
    }

    // Segment boundaries + final exit (each fires once). Boundary triggers sit
    // HANDOFF_TRIGGER_LEAD left of each authored meeting point; the exit is authored.
    if (!this.firedHandoff[0] && this.currentChar === "dan" && this.player.x >= this.meets[0] - HANDOFF_TRIGGER_LEAD) {
      this.firedHandoff[0] = true;
      this.handoff(0);
    } else if (
      !this.firedHandoff[1] &&
      this.currentChar === "lucas" &&
      this.player.x >= this.meets[1] - HANDOFF_TRIGGER_LEAD
    ) {
      this.firedHandoff[1] = true;
      this.handoff(1);
    } else if (!this.won && this.currentChar === "andrew" && this.player.x >= this.exitX) {
      this.winRelay();
    }
  }

  /** Drive the current runner's per-frame animation from its physics state. */
  private driveAnim() {
    if (this.currentChar === "dan") {
      updateCharacterAnim(this.player, 0); // throwUntil 0: Dan never throws here
      return;
    }
    if (this.currentChar === "lucas") {
      updateLucasAnim(this.player);
      return;
    }
    // Andrew has no per-frame update fn; drive it inline.
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const vx = body.velocity.x;
    const moving = Math.abs(vx) > 5;
    if (moving) this.player.setFlipX(vx < 0);
    const cur = this.player.anims.currentAnim?.key;
    if (!body.blocked.down) {
      this.restoreAndrewSize();
      if (cur !== AA.jump) this.player.play(AA.jump, true);
    } else if (moving) {
      this.restoreAndrewSize();
      if (cur !== AA.run || !this.player.anims.isPlaying) this.player.play(AA.run, true);
    } else {
      // Standing still: rest on the idle pose, shrunk to the running height so he
      // doesn't pop bigger, with the origin compensated to keep his feet planted.
      this.player.anims.stop();
      this.player.setFrame("andrew-idle-0");
      const s1 = this.andrewBaseScale * ANDREW_IDLE_MATCH;
      if (this.player.scaleX !== s1) {
        // Keep the frame bottom (his feet) at the same world y after scaling:
        // (1 - originY) * scale is held constant.
        const originY = 1 - (this.andrewBaseScale / s1) * (1 - this.andrewBaseOriginY);
        this.player.setScale(s1);
        this.player.setOrigin(this.andrewBaseOriginX, originY);
      }
    }
  }

  /** Restore Andrew's base render transform after the idle size-match override. */
  private restoreAndrewSize() {
    if (this.player.scaleX !== this.andrewBaseScale) {
      this.player.setScale(this.andrewBaseScale);
      this.player.setOrigin(this.andrewBaseOriginX, this.andrewBaseOriginY);
    }
  }

  /** Andrew's per-frame animation in the relay: he auto-runs, so it's run vs jump vs
   *  the throw clip (never idle). Mirrors Level3AndrewScene.updateAndrewAnim: hold the
   *  throw clip until the token launches, then jump if airborne, else run. */
  private driveAndrewRunAnim(body: Phaser.Physics.Arcade.Body) {
    const cur = this.player.anims.currentAnim?.key;
    const throwing = this.time.now < this.andrewThrowUntil;
    const tokenLaunched = this.andrewThrownFor === this.andrewThrowUntil;
    if (throwing && !tokenLaunched) return; // hold the windup until the token leaves his hand
    if (!body.blocked.down) {
      this.restoreAndrewSize();
      if (cur !== AA.jump) this.player.play(AA.jump, true);
      return;
    }
    if (throwing) return; // grounded: let the throw clip play its follow-through
    this.restoreAndrewSize();
    if (cur !== AA.run || !this.player.anims.isPlaying) this.player.play(AA.run, true);
  }

  /** POST_UPDATE: while Andrew is mid-throw, hold the Claude token in his hand through
   *  the windup (per the authored per-frame offsets), then launch it once at the release
   *  frame. He always faces right. No-op outside his segment. Mirrors Level3AndrewScene. */
  private updateAndrewThrowBall() {
    if (this.currentChar !== "andrew") return;
    const st = this.time.now < this.andrewThrowUntil ? andrewThrowBall(this.player) : null;
    if (!st || !this.player.visible) {
      this.andrewHeldBall?.setVisible(false);
      return;
    }
    if (!st.released) {
      // Frame-local px offsets (throw.html), scaled by the sprite's on-screen scale.
      const x = this.player.x + st.ox * this.player.scaleX;
      const y = this.player.y + st.oy * this.player.scaleY;
      if (!this.andrewHeldBall) this.andrewHeldBall = this.add.image(x, y, Tex.throwClaude);
      this.andrewHeldBall
        .setScale(ANDREW_TOKEN_PX / Math.max(this.andrewHeldBall.width, this.andrewHeldBall.height))
        .setPosition(x, y)
        .setVisible(true)
        .setDepth(this.player.depth + (st.front ? 1 : -1));
      return;
    }
    this.andrewHeldBall?.setVisible(false);
    if (this.andrewThrownFor !== this.andrewThrowUntil) {
      this.andrewThrownFor = this.andrewThrowUntil; // one launch per throw
      this.launchAndrewToken(this.player.x + st.launchOx * this.player.scaleX, this.player.y + st.launchOy * this.player.scaleY);
    }
  }

  /** Launch a thrown Claude token from the hand position at the release frame. The relay
   *  has no bugs to hit, so it's a celebratory flourish: it spins away and is cleaned up. */
  private launchAndrewToken(x: number, y: number) {
    const p = this.physics.add.sprite(x, y, Tex.throwClaude);
    p.setScale(ANDREW_TOKEN_PX / Math.max(p.width, p.height));
    p.setDepth(this.player.depth + 1);
    p.setVelocity(980, 20); // flat and fast down the track; world gravity drops it
    p.setAngularVelocity(540); // spin as it flies
    this.time.delayedCall(1400, () => p.destroy());
  }

  /** Begin a handoff. `i` indexes RELAY_ORDER pairs: 0 = dan→lucas, 1 = lucas→andrew. */
  private handoff(i: number) {
    this.locked = true;
    // Never carry a climb (gravity off) into the auto-run handoff.
    this.climbing = false;
    (this.player.body as Phaser.Physics.Arcade.Body).setAllowGravity(true);
    this.phase = `handoff-${i}-out`;
    this.cameras.main.stopFollow(); // camera holds steady, then pans, across the swap
    this.meetX = this.meetXFor(i); // fixed meeting point (where the receiver waits)
    this.runningOut = true;
  }

  /** Outgoing runner has reached the meeting point. Swap the player for the giver's
   *  tag-out sprite (so the outgoing runner STAYS on screen), slap, then let the
   *  already-waiting receiver play its tag-in. Both runners are visible the whole
   *  time and the giver is left standing afterwards; nobody pops in or out. */
  private beginTagOut() {
    const i = this.phase.startsWith("handoff-1") ? 1 : 0;
    const nextId = RELAY_ORDER[i + 1].id as CharId;
    const giver = this.givers[i];
    const receiver = this.receivers[i]; // has been standing here on frame 0

    // Hand the outgoing player's spot to its giver sprite (same pose, high-five art).
    // Capture the outgoing runner + the exact scale it renders at, so once the slap is
    // done we can leave the giver standing in that character's idle pose at the same size.
    const giverId = RELAY_ORDER[i].id as CharId;
    const giverScaleX = Math.abs(this.player.scaleX);
    const giverScaleY = this.player.scaleY;
    // Seat the giver exactly where the outgoing runner is standing RIGHT NOW (its real
    // feet), not via floorYAt: a handoff column can have an overhang wall (collides tiles
    // from the top), and floorYAt returns the topmost solid tile, which would fling the
    // giver up to the wall top and off-screen. The player's own feet are the ground truth.
    const outBody = this.player.body as Phaser.Physics.Arcade.Body;
    const giverX = this.player.x;
    const giverFeetY = outBody.bottom;
    this.player.setVelocity(0, 0);
    this.player.setVisible(false);
    giver.setVisible(true);
    giver.setPosition(giverX, giverFeetY);

    // Pan the camera to exactly where it will REST once control passes to the incoming
    // runner. After the tag-in we startFollow(player), which centres on the new runner
    // (standing at receiver.x); if the pan ended anywhere else the follow would snap the
    // view across on the first frame of control, reading as a jolt. So we pan to the
    // incoming runner's centre now, and startFollow later finds the camera already there.
    // Follow is already stopped, so the pan owns the camera until we startFollow again.
    const cam = this.cameras.main;
    const halfW = cam.width / 2;
    const camTarget = Phaser.Math.Clamp(receiver.x, halfW, this.worldWidth - halfW);
    const camMidY = cam.scrollY + cam.height / 2; // keep the current vertical framing during the pan
    cam.pan(camTarget, camMidY, HANDOFF_PAN_MS, "Sine.easeInOut");

    // Giver slaps (tag-out) while the receiver waits on frame 0. The receiver raises
    // to meet the hand (tag-in) starting TAG_IN_LEAD_MS before the tag-out finishes,
    // so the two hands connect in the middle rather than the clips playing strictly
    // back-to-back. Both stay on screen; once the giver finishes the slap it settles
    // into that character's idle pose (the tag-out sheets are pure run cycles, so their
    // own frames never show a standing pose).
    const tagOutKey = RELAY_ORDER[i].tagOut!;
    const tagInKey = RELAY_ORDER[i + 1].tagIn!;
    this.playTagOn(giver, tagOutKey, () => this.standGiverIdle(giver, giverId, giverScaleX, giverScaleY, giverFeetY));

    const tagOutMs = this.tagDurationMs(tagOutKey);
    const lead = TAG_IN_LEAD_MS[nextId] ?? 0;
    const startTagInAfter = Phaser.Math.Clamp(tagOutMs - lead, 0, tagOutMs);
    this.time.delayedCall(startTagInAfter, () => {
      this.phase = `handoff-${i}-in`;
      this.playTagOn(receiver, tagInKey, () => {
        // Control passes to the incoming runner, standing where the receiver stood.
        this.setCharacter(nextId);
        this.placeOnFloor(receiver.x); // feet on the tile floor (never inside it)
        this.player.setVisible(true);
        receiver.setVisible(false); // now embodied by the live player sprite
        // giver is left visible, relaxed back to its idle start frame (see above).
        cam.startFollow(this.player, true, 0.1, 0.1);
        this.locked = false;
        this.phase = nextId;
        // Right after the high-five, Kirby cheers the runners on.
        this.kirbyCelebrate();
      });
    });
  }

  /** Playback length (ms) of a one-shot tag clip at the relay's TAG_FPS override.
   *  playTagOn plays every tag at TAG_FPS, so duration is frames / TAG_FPS. */
  private tagDurationMs(key: RelayKey): number {
    return (RELAY[key].frames / TAG_FPS) * 1000;
  }

  /** Kirby's video call, pinned to the TOP-CENTRE of the camera for the whole relay.
   *  He idles / sips water during the run (occasional, not constant) and cheers right
   *  after each high-five. This replaces the old full-screen Finale scene. */
  private showKirbyCall() {
    if (this.kirby) return;
    const panelW = 230;
    const panelH = 140;
    const px = 14 + panelW / 2; // bottom-left
    const py = this.scale.height - 14 - panelH / 2;
    const left = px - panelW / 2;
    const right = px + panelW / 2;
    const top = py - panelH / 2;
    const bottom = py + panelH / 2;

    // Zoom-style skyline background behind his video (sized to the panel), then the
    // transparent Kirby cutout in front so the skyline reads as his virtual bg.
    this.add
      .image(px, py, "kirby-bg")
      .setDisplaySize(panelW, panelH)
      .setScrollFactor(0)
      .setDepth(1500);
    registerPortraitAnims(this, KIRBY_ANIMS);
    // Anchor the portrait to the panel's bottom edge (webcam framing: his shoulders
    // meet the bottom), scaled up so he fills the call.
    this.kirby = this.add
      .sprite(px, bottom - KIRBY_Y_NUDGE, "kirby-blink", 0)
      .setOrigin(0.5, 1)
      .setScale(KIRBY_SCALE)
      .setScrollFactor(0)
      .setDepth(1501);

    // Panel border on top of the video.
    this.add
      .rectangle(px, py, panelW, panelH, 0x000000, 0)
      .setStrokeStyle(2, Palette.kirby)
      .setScrollFactor(0)
      .setDepth(1502);

    // Name plate, bottom-left, white on a subtle dark strip for legibility.
    const name = this.add
      .text(left + 8, bottom - 6, "Kirby", { fontFamily: "monospace", fontSize: "13px", color: "#ffffff" })
      .setOrigin(0, 1)
      .setScrollFactor(0)
      .setDepth(1503);
    this.add
      .rectangle(left + 6, bottom - 4, name.width + 8, name.height + 4, 0x000000, 0.5)
      .setOrigin(0, 1)
      .setScrollFactor(0)
      .setDepth(1502);

    // "LIVE" tag, top-right: small uppercase label with a blinking red dot in the corner.
    const dot = this.add
      .circle(right - 12, top + 13, 4, 0xff3b30)
      .setScrollFactor(0)
      .setDepth(1503);
    this.tweens.add({ targets: dot, alpha: { from: 1, to: 0.15 }, duration: 500, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    this.add
      .text(dot.x - 9, dot.y, "LIVE", { fontFamily: "monospace", fontSize: "10px", color: "#ffffff", fontStyle: "bold" })
      .setOrigin(1, 0.5)
      .setScrollFactor(0)
      .setDepth(1503);

    this.kirbyRest(); // hold the open-eyes resting frame; he stays mostly still
    this.scheduleKirbyAmbient();
    this.scheduleKirbyBlink();
  }

  /** Hold Kirby's default resting pose: the open-eyes blink frame, no animation.
   *  Always resets to the blink sheet so he rests with his eyes open regardless of
   *  which action ran last. */
  private kirbyRest() {
    if (!this.kirby) return;
    this.kirby.anims.stop();
    this.kirby.setTexture("kirby-blink", 0);
  }

  /** Schedule Kirby's next blink after a short randomized pause, so he blinks on a
   *  natural, visible cadence. */
  private scheduleKirbyBlink() {
    this.kirbyBlinkTimer = this.time.delayedCall(Phaser.Math.Between(...KIRBY_BLINK_MS), () => this.kirbyBlink());
  }

  /** Play one blink, then re-arm. Skipped while celebrating or mid bigger action so
   *  it never interrupts them; the blink yoyos back to the open-eyes frame on its own,
   *  so no explicit rest is needed after it. */
  private kirbyBlink() {
    if (this.kirby && !this.kirbyCelebrating && !this.kirbyActing) {
      // repeat: 0 so it yoyos open->closed->open exactly once (the anim registers
      // with repeat -1 by default, which would loop the blink forever).
      this.kirby.play({ key: "kirby-blink", repeat: 0 }, true);
    }
    this.scheduleKirbyBlink();
  }

  /** Schedule the next bigger idle action after a deliberate pause (~5s), so Kirby
   *  only sips/claps occasionally. */
  private scheduleKirbyAmbient() {
    this.kirbyTimer = this.time.delayedCall(Phaser.Math.Between(4500, 5500), () => this.kirbyAmbient());
  }

  /** Play one bigger idle action (sip / clap) once, then go still again. Skipped
   *  while celebrating or mid-blink so it doesn't cut those off; always re-arms. */
  private kirbyAmbient() {
    const midBlink = !!this.kirby?.anims.isPlaying && this.kirby.anims.currentAnim?.key === "kirby-blink";
    if (this.kirby && !this.kirbyCelebrating && !midBlink) {
      const action = KIRBY_IDLE_ACTIONS[Phaser.Math.Between(0, KIRBY_IDLE_ACTIONS.length - 1)];
      this.kirbyActing = true;
      this.kirby.play({ key: action, repeat: 0 }, true);
      this.kirby.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        this.kirbyActing = false;
        if (!this.kirbyCelebrating) this.kirbyRest();
      });
    }
    this.scheduleKirbyAmbient();
  }

  /** Play an encouraging cheer/clap right after a high-five, then settle back to still. */
  private kirbyCelebrate() {
    if (!this.kirby) return;
    this.kirbyCelebrating = true;
    this.kirbyActing = false; // a cheer overrides any in-progress idle action
    const cheer = KIRBY_CHEERS[Phaser.Math.Between(0, KIRBY_CHEERS.length - 1)];
    this.kirby.play(cheer, true);
    this.time.delayedCall(3200, () => {
      this.kirbyCelebrating = false;
      this.kirbyRest();
    });
  }

  /** Create a tag sprite (feet on the floor at `x`), scaled to the stage height and
   *  resting on frame 0 until it is told to play. */
  private makeTag(key: RelayKey, x: number): Phaser.GameObjects.Sprite {
    const n = TAG_NUDGE[key] ?? { dx: 0, dy: 0 };
    return this.add
      .sprite(x + n.dx, this.floorYAt(x) + n.dy, key, 0)
      .setOrigin(0.5, 1)
      .setDepth(11)
      // Scale by the art's measured body height (not the frame) so every runner is
      // RELAY_PERSON_H tall regardless of how big it's drawn in its sheet.
      .setScale(RELAY_PERSON_H / TAG_BODY_H[key]);
  }

  /** Play a one-shot tag animation on an existing sprite; run `done` on completion. */
  private playTagOn(sprite: Phaser.GameObjects.Sprite, key: RelayKey, done: () => void) {
    sprite.play({ key, repeat: 0, frameRate: TAG_FPS });
    sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, done);
  }

  /** World x where handoff `i` (0 = dan→lucas, 1 = lucas→andrew) meets: the authored
   *  handoff marker in relay.tmj, where the outgoing giver stops to tag out. */
  private meetXFor(i: number): number {
    return this.meets[i];
  }

  /** World x where handoff `i`'s incoming receiver waits (a touch right of the giver). */
  private receiverXFor(i: number): number {
    return this.meetXFor(i) + RECEIVER_GAP;
  }

  /** Andrew reached the end: win. Show the conquered banner, then the post-relay cutscene → Ending. */
  private winRelay() {
    if (this.won) return;
    this.won = true;
    this.phase = "won";
    this.kirbyTimer?.remove();
    this.kirbyBlinkTimer?.remove();
    GameState.setFinaleCleared();
    this.scene.start("RegionConquered", { next: "Dialogue", nextData: { scriptKey: "post-relay", next: "Ending" } });
  }
}
